import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import './nodeStyles.css';

export interface ExtractSkillsNodeData {
  isProcessing?: boolean;
  isComplete?: boolean;
  isMuted?: boolean;
  isClickable?: boolean;
  thinkingMessage?: string;
  status?: 'idle' | 'queued' | 'processing' | 'processed' | 'violation' | 'failed';
  violations?: string[];
  extractedSkills?: string[];
  onRetry?: () => void;
  onClick?: () => void;
}

const ExtractSkillsNode: React.FC<NodeProps> = ({ data }) => {
  const {
    isProcessing, isComplete, isMuted, isClickable, thinkingMessage,
    status, violations, extractedSkills, onRetry, onClick,
  } = (data as unknown as ExtractSkillsNodeData) || {};

  const isViolation = status === 'violation';
  const isFailed = status === 'failed';

  const nodeClasses = [
    'rf-node rf-node--extract-pill',
    isProcessing ? 'rf-node--processing' : '',
    isComplete ? 'rf-node--complete' : '',
    isViolation ? 'rf-node--violation' : '',
    isFailed ? 'rf-node--failed' : '',
    isMuted ? 'rf-node--muted' : '',
  ].filter(Boolean).join(' ');

  const pillClasses = [
    'rf-pill rf-pill--extract rf-pill--wide',
    isProcessing ? 'rf-pill--processing' : '',
    isComplete ? 'rf-pill--complete' : '',
    isViolation ? 'rf-pill--violation' : '',
    isFailed ? 'rf-pill--failed' : '',
    isMuted ? 'rf-pill--muted' : '',
    isComplete && isClickable ? 'rf-pill--clickable' : '',
  ].filter(Boolean).join(' ');

  const renderIcon = () => {
    if (isProcessing) {
      return <div className="rf-pill__loader" />;
    }
    if (isComplete) {
      return (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }
    if (isViolation) {
      return (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    }
    if (isFailed) {
      return (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    }
    return (
      <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0 C13.5 7 17 10.5 24 12 C17 13.5 13.5 17 12 24 C10.5 17 7 13.5 0 12 C7 10.5 10.5 7 12 0Z" />
      </svg>
    );
  };

  const renderSubtitle = () => {
    if (isProcessing) return thinkingMessage || 'Processing...';
    if (isComplete && extractedSkills && extractedSkills.length > 0) {
      return `${extractedSkills.length} items extracted`;
    }
    if (isComplete) return 'Complete';
    if (isViolation) return 'Invalid content';
    if (isFailed) return 'Failed';
    return 'Waiting...';
  };

  return (
    <div className={nodeClasses}>
      <Handle type="target" position={Position.Left} id="left" className="rf-handle rf-handle--target rf-handle--pill-center" />

      <div
        className={pillClasses}
        onClick={isComplete && isClickable && onClick ? () => onClick() : undefined}
        onKeyDown={
          isComplete && isClickable && onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        role={isComplete && isClickable && onClick ? 'button' : undefined}
        tabIndex={isComplete && isClickable && onClick ? 0 : undefined}
      >
        <div className={`rf-pill__icon rf-pill__icon--extract ${isProcessing ? 'rf-pill__icon--processing' : ''} ${isComplete ? 'rf-pill__icon--complete' : ''} ${isViolation ? 'rf-pill__icon--violation' : ''} ${isFailed ? 'rf-pill__icon--failed' : ''}`}>
          {renderIcon()}
        </div>

        <div className="rf-pill__content">
          <span className="rf-pill__title rf-pill__title--extract">
            {isViolation ? 'Content Issue' : isFailed ? 'Extraction Error' : 'Extracted Info'}
          </span>
          <span className={`rf-pill__subtitle rf-pill__subtitle--extract ${isProcessing ? 'rf-pill__subtitle--processing' : ''}`}>
            {renderSubtitle()}
          </span>
        </div>

        {isComplete && (
          <span className="rf-pill__badge rf-pill__badge--complete">
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        )}

        {isFailed && onRetry && (
          <button
            className="rf-pill__retry-btn"
            onClick={(e) => { e.stopPropagation(); onRetry(); }}
            title="Retry extraction"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        )}
      </div>

      {/* Violations tooltip */}
      {isViolation && violations && violations.length > 0 && (
        <div className="rf-pill__violations">
          {violations.map((v, i) => (
            <div key={i} className="rf-pill__violation-item">{v}</div>
          ))}
        </div>
      )}

      {/* Skills preview on complete */}
      {isComplete && extractedSkills && extractedSkills.length > 0 && (
        <div className="rf-pill__skills-preview">
          {extractedSkills.slice(0, 5).map((s, i) => (
            <span key={i} className="rf-pill__skill-tag">{s}</span>
          ))}
          {extractedSkills.length > 5 && (
            <span className="rf-pill__skill-more">+{extractedSkills.length - 5} more</span>
          )}
        </div>
      )}

      <Handle type="source" position={Position.Right} id="right" className="rf-handle rf-handle--source rf-handle--pill-center" />
    </div>
  );
};

export default memo(ExtractSkillsNode);
